import {
  getFrontendConfigLoader,
  type ConfigLoader,
  type FrontendConfigurationSchema,
} from '@doxyme/config';

export type ResolvedFrontendGraphqlUrls = {
  /** Single GraphQL HTTP endpoint: Apollo Router (default) or api-core when the router is disabled. */
  graphqlUrl: string;
};

/**
 * When `USE_APOLLO_ROUTER_URL` is explicitly `"false"` (case-insensitive), the app
 * uses `API_CORE_GRAPHQL_URL` (api-core GraphQL, including `ExtensionsRegistry`).
 * Any other value (including missing or empty) keeps the safe default: Apollo Router.
 */
export function usesApolloRouterForGraphql(
  config: ConfigLoader<FrontendConfigurationSchema> = getFrontendConfigLoader(),
): boolean {
  const raw = config.getOptional('USE_APOLLO_ROUTER_URL');

  const normalizedValue = String(raw).trim().toLowerCase();
  if (normalizedValue === 'false') {
    return false;
  }

  return true;
}

export function getResolvedFrontendGraphqlUrls(
  config: ConfigLoader<FrontendConfigurationSchema> = getFrontendConfigLoader(),
): ResolvedFrontendGraphqlUrls {
  if (usesApolloRouterForGraphql(config)) {
    const router = config.getOptional('APOLLO_ROUTER_GRAPHQL_URL');
    if (router === undefined || String(router).trim() === '') {
      throw new Error('Apollo endpoint url is undefined');
    }
    return { graphqlUrl: String(router) };
  }

  const core = config.getOptional('API_CORE_GRAPHQL_URL');

  if (core === undefined || String(core).trim() === '') {
    throw new Error(
      'API_CORE_GRAPHQL_URL is undefined or empty when USE_APOLLO_ROUTER_URL is false',
    );
  }

  return { graphqlUrl: String(core) };
}
